////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: Home stats ticker — spring count-up on scroll-into-view         //
//           (1.0.3.md §6, "the signature clever drop-in")                   //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////
//
//  Each figure counts up from 0 with a spring once its own number scrolls into
//  view (`useInView`, matching lib/motion.ts's `VIEWPORT_ONCE` contract:
//  fires once, `-10%` margin) — reduced motion renders the final value
//  immediately, no animation, per §7's non-negotiable acceptance criterion.
//  `animate()` is framer-motion's own imperative tween helper (already in the
//  dependency tree; no new package) rather than a hand-rolled rAF loop.
//
//  The animated digits are `aria-hidden` with a static `sr-only` twin carrying
//  the FINAL value — a rapidly-changing number is a poor thing to expose to
//  assistive tech, which would otherwise announce every intermediate frame.
//
////////////////////////////////////////////////////////////////////////////////

import { useEffect, useRef, useState } from 'react';
import { animate, motion, useInView } from 'framer-motion';

import { useReducedMotion } from '@/hooks/useReducedMotion';
import { COUNT_UP_SPRING, fadeRise, reduceVariants, staggerGroup, VIEWPORT_ONCE } from '@/lib/motion';

export interface Stat {
  id: string;
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
}

// MARK — Founded year is genuinely sourced (content/pages.ts's `about` bodyMd:
// Luke Skurrie "moved to found Ironside in 2016"), not invented — "Years
// delivering" below is computed from it, not hand-typed. `PROJECTS_DELIVERED`
// and `SQM_DELIVERED`, by contrast, are NOT real figures: the API's
// `/public/projects` route is honestly empty pending marketing content
// (pages/public/Projects.tsx), and there is no m² figure anywhere in the
// content pipeline. These two are placeholder constants standing in for real
// numbers Mike/marketing need to supply — flagged here for ratification
// rather than presented as sourced data, same convention as config.tsx's
// placeholder background-asset MARKs.
const FOUNDED_YEAR = 2016;
const PROJECTS_DELIVERED_PLACEHOLDER = 60;
const SQM_DELIVERED_PLACEHOLDER = 250_000;

function currentStats (): Stat[] {
  return [
    { id: 'years', label: 'Years delivering', value: new Date().getFullYear() - FOUNDED_YEAR, suffix: '+' },
    { id: 'projects', label: 'Projects delivered', value: PROJECTS_DELIVERED_PLACEHOLDER, suffix: '+' },
    { id: 'sqm', label: 'm² delivered', value: SQM_DELIVERED_PLACEHOLDER, suffix: '+' },
  ];
}

function StatFigure ({ stat, reducedMotion }: { stat: Stat; reducedMotion: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, VIEWPORT_ONCE);
  const [display, setDisplay] = useState(reducedMotion ? stat.value : 0);

  useEffect(() => {
    if (!inView || reducedMotion) return;

    const controls = animate(0, stat.value, {
      ...COUNT_UP_SPRING,
      onUpdate: (value) => setDisplay(Math.round(value)),
    });
    return () => controls.stop();
  }, [inView, reducedMotion, stat.value]);

  const formatted = `${stat.prefix ?? ''}${display.toLocaleString('en-AU')}${stat.suffix ?? ''}`;
  const finalValue = `${stat.prefix ?? ''}${stat.value.toLocaleString('en-AU')}${stat.suffix ?? ''}`;

  return (
    <div ref={ref} className="text-center sm:text-left">
      <p aria-hidden="true" className="text-5xl font-light text-ink sm:text-6xl">{formatted}</p>
      <span className="sr-only">{finalValue}</span>
      <p className="mt-2 text-sm text-muted">{stat.label}</p>
    </div>
  );
}

export function StatsTicker () {
  const reducedMotion = useReducedMotion();
  const groupVariants = reduceVariants(staggerGroup, reducedMotion);
  const itemVariants = reduceVariants(fadeRise, reducedMotion);

  return (
    <motion.div
      className="grid grid-cols-1 gap-10 sm:grid-cols-3"
      variants={groupVariants}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_ONCE}
    >
      {currentStats().map((stat) => (
        <motion.div key={stat.id} variants={itemVariants}>
          <StatFigure stat={stat} reducedMotion={reducedMotion} />
        </motion.div>
      ))}
    </motion.div>
  );
}
